import React, { Component } from 'react'
import ReactToPrint, { PrintContextConsumer } from 'react-to-print'
import api from '../api/api.js'

import { withStyles } from '@mui/styles'
import {
  Alert,
  AppBar,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Snackbar,
  Slide,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'

import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'
import FilterListIcon from '@mui/icons-material/FilterList'
import LaunchIcon from '@mui/icons-material/Launch'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

import { styles } from './authorize/authStyles.js'
import AuthorizeFilterModal from './authorize/authorizeFilterModal.jsx'
import AssessmentEditor from './visit/AssessmentEditor.jsx'
import AuthorizePages from './authorize/AuthorizePages.jsx'
import InvoicePages from './authorize/InvoicePages.jsx'
import { getStoredAuthorizeFilters } from '../util/visitFilters'
import {
  buildAuthorizationAssessmentRows,
  buildAuthorizationEmailHtml as buildAuthorizationEmailHtmlTemplate,
  buildInvoiceEmailHtml as buildInvoiceEmailHtmlTemplate,
} from './authorize/emailTemplates.js'
import logo from './img/logo.png'

const DEMO_MAIL_CONFIG_SET = 'runtimeconfigYahoo.json'



const AUTHORIZE = 1
const INVOICE = 2
const TREATCODE88100 = '88100 - R266.70 - home visit'
const TREATCODE88002 = '88002 - R540.50 - consultation, wound assessment, and counseling'
const TREATCODE88041 = '88041 - R205.60 - treatment of extensive wounds, charged on every visit'
const ICD10CODES = 'Z48.0, T81.4, Y83.8'
const TARIFFCODE = '88301'

const hMatches = window.matchMedia("(max-height: 500px)").matches
const wMatches = window.matchMedia("(max-width: 400px)").matches
const pcMatches = window.matchMedia("(min-width: 1200px) and (min-height: 900px)").matches

const PHONEHEIGHT = 470
const PHONEWIDTH = 450
const TABHEIGHT = 480
const TABWIDTH = 750
const PCHEIGHT = 600
const PCWIDTH = 750

const vHeight = hMatches ? PHONEHEIGHT : (pcMatches ? PCHEIGHT : TABHEIGHT)
const vWidth = wMatches ? PHONEWIDTH : (pcMatches ? PCWIDTH : TABWIDTH)

const assessment0 = {
  // Legacy fields
  allergies: '',
  balance: 'unknown',
  comorbidities: '',
  depth: 1,
  education: '',
  exudateLevel: '1',
  exudateType: '',
  healingFactors: '',
  healthEducation: '',
  infection: false,
  inflammation: false,
  length: 1,
  margin: 'unknown',
  medInfo: '',
  medications: '',
  mobility: '',
  nutrition: '',
  painLevel: 0,
  postsurgical: false,
  site: '',
  smoking: '',
  treatment: '',
  treatmentPlan: '',
  type: '',
  viability: 'unknown',
  patientId: '',
  visitId: '',
  weight: 0,
  width: 1,
  // New assessment overlay fields
  woundStartDate: '',
  treatmentStartDate: '',
  woundLocation: '',
  diabeticUlcer: false,
  pressureUlcer: false,
  venousUlcer: false,
  trauma: false,
  burns: false,
  other: false,
  surgicalProc: '',
  dstage: '',
  pstage: '',
  vstage: '',
  traumaDesc: '',
  burnDegree1: false,
  burnDegree2: false,
  burnDegree3: false,
  burnArea: '',
  cellulitis: false,
  colonised: false,
  superficial: false,
  nerds: {},
  stones: {},
  serous: '',
  sanguineous: '',
  serosang: '',
  purulent: '',
  otherDesc: '',
  // Discovery wound assessment fields
  outpatientPracticeNo: '',
  bodyAnnotation: '',
  fistulae: false,
  pilonidalCyst: false,
  skinCancer: false,
  arterialUlcer: false,
  gangren: false,
  skinGrafting: false,
  healableWound: false,
  maintenanceWound: false,
  nonHealableWound: false,
  healabilityOther: '',
  siteOfWound: '',
  sizeOfWound: '',
  woundLength: '',
  woundWidth: '',
  woundDepth: '',
  woundVolume: '',
  woundUndermining: '',
  epithelialisation: false,
  granulation: false,
  slough: false,
  blackNecrotic: false,
  overGranulating: false,
  fungating: false,
  mixedTissue: false,
  biofilm: false,
  exudateColour: '',
  exudateAmount: '',
  skinHealthy: false,
  skinDry: false,
  skinDiscoloured: false,
  skinErythema: false,
  skinMacerated: false,
  skinCallous: false,
  skinOedematous: false,
  woundPhaseInflammatory: false,
  woundPhaseProliferation: false,
  woundPhaseMaturation: false,
  painFrequency: '',
  treatmentGoals: {},
  treatmentDates: '',
  includePhoto: false,
  clinicalMotivation: false,
  quotation: false,
  assessmentSignatureDataUrl: '',
  assessmentSignatureDate: '',
}

const visitColumns = [
  { field: 'date', headerName: 'Date', width: 120, editable: false },
  { field: 'patientName', headerName: 'Surname', width: 160, editable: false },
  { field: 'firstName', headerName: 'First Name', width: 160, editable: false },
  { field: 'SAID', headerName: 'ID/Passport', width: 160, editable: false },
  { field: 'medicalAid', headerName: 'Medical Aid', width: 160, editable: false },
  { field: 'memberNo', headerName: 'Membership No.', width: 150, editable: false },
]

const prodListColumns = [
  { field: 'tariffCode', headerName: 'Tariff Code', width: 100, align: 'left', editable: false },
  { field: 'nappiCode', headerName: 'Nappi Code', width: 115, align: 'left', editable: false },
  { field: 'title', headerName: 'Materials', width: 450, align: 'left', editable: false },
  { field: 'quantity', headerName: 'Qty', width: 100, align: 'right', editable: true },
  { field: 'amount', headerName: 'Cost, excl. VAT', width: 124, align: 'right', editable: true },
]

const prodListColumnsInv = [
  { field: 'date', headerName: 'Date of Service', width: 110, editable: true },
  { field: 'tariffCode', headerName: 'Tariff Code', width: 86, editable: false },
  { field: 'nappiCode', headerName: 'Nappi Code', width: 106, editable: true },
  { field: 'title', fontSize: 16, headerName: 'Materials', width: 370, editable: true },
  { field: 'ICD10Codes', headerName: 'ICD10 Codes', width: 154, default: '', editable: true },
  { field: 'quantity', headerName: 'Qty', width: 60, editable: true },
  { field: 'amount', headerName: 'Cost (vat incl.)', width: 100, editable: true },
]

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" {...props} ref={ref} />
))

function formatNumber(number) {
  number = number.toFixed(2) + ''
  let x = number.split('.')
  let x1 = x[0]
  let x2 = x.length > 1 ? '.' + x[1] : ''
  let rgx = /(\d+)(\d{3})/
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1,$2')
  }
  return x1 + x2
}

class authorize extends Component {
  constructor(props) {
    super(props)

    this.state = {
      patient: {
        firstName: 'Bami',
        gender: 'Ms.',
        ICD10Codes: ICD10CODES,
        medicalAid: 'Fedhealth',
        memberNo: '111',
        mobile: '082',
        patientName: 'Goreng',
        referringDrNo: '111',
        referringDoctor: 'Dr. Who',
        SAID: '111',
      },
      assessment: {},
      AuthButtOn: false,
      dateString: 'dateString',
      error: false,
      emailSending: false,
      filterModalOpen: false,
      medicalAidEmail: '',
      medicalAidRequiresDiscoveryPdf: false,
      medicalAids: [],
      visitFilters: getStoredAuthorizeFilters(),
      fullName: 'Ms. Bami Goreng',
      groupDetails: {},
      images: [],
      InvoiceButtOn: false,
      login: false,
      paLoading: false,
      phLoading: false,
      printAuthOpen: false,
      printInvoiceOpen: false,
      products: [],
      prodList: [],
      sbLoading: false,
      snackbarMessage: '',
      snackbarOpen: false,
      snackbarSeverity: 'info',
      selectedRows: [],
      supplRows1: [],
      totals: 0,
      viLoading: false,
      visitsMap: {},
      visits: [],
    }

    this.componentRef = null
    this.discoveryPdfRef = null
    this.logoInlineAttachment = null
  }

  getLogoInlineAttachment = async () => {
    if (this.logoInlineAttachment) {
      return this.logoInlineAttachment
    }

    try {
      const response = await fetch(logo)
      if (!response.ok) {
        return ''
      }

      const blob = await response.blob()
      const dataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('Failed to read logo data'))
        reader.readAsDataURL(blob)
      })

      if (typeof dataUri !== 'string') {
        return null
      }

      const matches = dataUri.match(/^data:(.+);base64,(.+)$/)
      if (!matches) {
        return null
      }

      this.logoInlineAttachment = {
        cid: 'invoice-logo',
        contentBase64: matches[2],
        contentType: matches[1],
        filename: 'logo.png',
      }
      return this.logoInlineAttachment
    } catch (e) {
      return null
    }
  }

  componentDidMount = () => {
    this.fetchVisits()
    this.fetchProducts()
    this.fetchMedicalAids()
  }

  getVisitRoute = () => {
    const { SAID, memberNo, patientName, monthYear, recentDays } = this.state.visitFilters

    if (SAID) {
      return `/visits/group/${this.props.groupId}/SAID/${encodeURIComponent(SAID)}`
    }
    if (memberNo) {
      return `/visits/group/${this.props.groupId}/memberNo/${encodeURIComponent(memberNo)}`
    }
    if (patientName) {
      return `/visits/group/${this.props.groupId}/name/${encodeURIComponent(patientName)}`
    }
    if (monthYear) {
      return `/visits/group/${this.props.groupId}/monthYear/${monthYear}`
    }
    if (recentDays) {
      return `/visits/group/${this.props.groupId}/days/${recentDays}`
    }
    return `/visits/group/${this.props.groupId}`
  }

  openFilterModal = () => {
    this.setState({ filterModalOpen: true })
  }

  closeFilterModal = () => {
    this.setState({ filterModalOpen: false })
  }

  handleFilterChanged = (filters) => {
    this.setState(
      { visitFilters: filters },
      this.fetchVisits,
    )
  }

  fetchVisits = async () => {
    const { SAID, memberNo, patientName, monthYear, recentDays } = this.state.visitFilters
    const hasActiveFilter = [SAID, memberNo, patientName, monthYear, recentDays]
      .some((value) => String(value || '').trim() !== '')

    if (!hasActiveFilter) {
      this.setState({
        visits: [],
        visitsMap: {},
        viLoading: false,
      })
      return
    }

    try {
      this.setState({ viLoading: true })
      const response = await api.get(this.getVisitRoute())
      const visitsMap = response.data.reduce((map, obj) => {
        const { id, ...rest } = obj
        map[id] = rest
        return map
      }, {})
      this.setState({
        visits: response.data,
        visitsMap,
        viLoading: false,
      })
    } catch (error) {
      if (error.response?.status === 403) {
        this.setState({ login: true })
      }
      this.setState({ viLoading: false })
      console.log(error)
    }
  }

  fetchMedicalAids = async () => {
    try {
      const response = await api.get(`/medicalAids/`)
      this.setState({ medicalAids: response.data || [] })
    } catch (error) {
      if (error.response?.status === 403) {
        this.setState({ login: true })
      }
      console.log(error)
    }
  }

  fetchProducts = async () => {
    try {
      this.setState({ productLoading: true })
      const response = await api.get(`/products/group/${this.props.groupId}`)
      this.setState({
        products: response.data,
        productLoading: false,
      })
    } catch (error) {
      if (error.response?.status === 403) {
        this.setState({ login: true })
      }
      this.setState({ productLoading: false })
      console.log(error)
    }
  }

  getPatient = async (visitId) => {
    let masterVisit = {}

    for (const visit of this.state.visits) {
      if (visitId === visit.id) {
        masterVisit = visit
        break
      }
    }

    const SAID = masterVisit.SAID

    let dateString = ''
    for (const selectedVisitId of this.state.selectedRows) {
      for (const visit of this.state.visits) {
        if (visit.id === selectedVisitId) {
          dateString = dateString + visit.date + ', '
        }
      }
    }
    dateString = dateString.substring(0, dateString.length - 2)
    this.setState({ dateString })

    try {
      this.setState({ paLoading: true })
      const response = await api.get(`/patients/${SAID}`)

      if (!response.data || response.data.length === 0) {
        this.setState({ paLoading: false })
        return null
      }

      const fullName =
        response.data[0].gender + ' ' +
        response.data[0].firstName + ' ' +
        response.data[0].patientName

      let ICD10Codes = ICD10CODES
      if (!(response.data[0].ICD10Codes === '' || response.data[0].ICD10Codes === undefined)) {
        ICD10Codes = response.data[0].ICD10Codes
      }

      const supplRows1 = (masterVisit.home === '1')
        ? [
            // this.createData('Treatment Dates:', dateString),
            this.createData('Treatment Dates: ', 'As per attached wound assessment form'),
            this.createData('Treatment Codes:', TREATCODE88100),
            this.createData(' ', TREATCODE88002),
            this.createData(' ', TREATCODE88041),
            this.createData('ICD10 Codes:', ICD10Codes),
          ]
        : [
            // this.createData('Treatment Dates:', dateString),
            this.createData('Treatment Dates: ', 'As per attached wound assessment form'),
            this.createData('Treatment Codes:', TREATCODE88002),
            this.createData(' ', TREATCODE88041),
            this.createData('ICD10 Codes:', ICD10Codes),
          ]

      const patient = { ...response.data[0] }
      patient.ICD10Codes = ICD10Codes
      this.getStockItems(ICD10Codes)

      const medicalAidRecord = this.state.medicalAids.find(
        (m) => m.name === patient.medicalAid
      )
      const medicalAidEmail = medicalAidRecord ? medicalAidRecord.email : ''
      const medicalAidRequiresDiscoveryPdf = !!medicalAidRecord?.requiresDiscoveryPdf

      this.setState({
        patient,
        medicalAidEmail,
        medicalAidRequiresDiscoveryPdf,
        paLoading: false,
        fullName,
        supplRows1,
      })

      return patient
    } catch (error) {
      if (error.response?.status === 403) {
        this.setState({ login: true })
      }
      this.setState({ paLoading: false })
      console.log(error)
      return null
    }
  }

  fetchImages = async (visitId) => {
    try {
      this.setState({ phLoading: true, images: [] })
      const response = await api.get(`/photoBatch/${visitId}`)
      this.setState({
        images: response.data || [],
        phLoading: false,
      })
    } catch (error) {
      if (error.response?.status === 403) {
        this.setState({ login: true })
      }
      this.setState({ phLoading: false })
      console.log(error)
    }
  }

  getStockItems = async (ICD10Codes) => {
    this.setState({ prodList: [], sbLoading: true })
    let totals = 0
    let prodList = []
    let x = this.state.selectedRows.length

    for (const row of this.state.selectedRows) {
      x -= 1
      const visitId = row
      const vDate = this.state.visitsMap[visitId].date

      try {
        const response = await api.get(`/stockBatch/${visitId}`)
        for (const rdata of response.data) {
          if (rdata.quantity === undefined) {
            rdata.quantity = 1
          }

          const targetName = String(rdata.product || '').trim().toLowerCase()
          for (const prod of this.state.products) {
            const productName = String(prod.title || '').trim().toLowerCase()
            if (targetName === productName) {
              let sp = Number(`${prod.sellingPrice}`) * rdata.quantity
              let id = Math.random().toString(36).substr(2, 16)
              let tariffCode = TARIFFCODE
              let nappiCode = prod.nappiCode

              if (nappiCode.substr(0, 2) === '88') {
                tariffCode = nappiCode
                nappiCode = ''
              }

              const newProd = {
                id,
                visitId,
                date: vDate,
                tariffCode,
                nappiCode: `${nappiCode}`,
                title: `${prod.title}`,
                ICD10Codes,
                quantity: rdata.quantity,
                amount: formatNumber(sp),
                amt: sp,
              }

              totals += sp
              prodList = [...prodList, newProd]
              break
            }
          }
        }

        this.setState({
          prodList,
          totals,
          sbLoading: (x > 0),
        })
      } catch (error) {
        if (error.response?.status === 403) {
          this.setState({ login: true })
        }
        this.setState({ sbLoading: false })
        console.log(error)
      }
    }
  }

  getAssessment = async (patientId) => {
    if (!patientId) {
      this.setState({ assessment: assessment0 })
      return
    }

    try {
      this.setState({ paLoading: true })
      const response = await api.get(`/assessment/patient/${patientId}`)
      let assessment = assessment0
      if (response.data.length > 0) {
        assessment = { ...assessment0, ...response.data[0] }
      }
      this.setState({
        assessment,
        paLoading: false,
      })
    } catch (error) {
      if (error.response?.status === 403) {
        this.setState({ login: true })
      }
      this.setState({ paLoading: false })
      console.log(error)
    }
  }

  getGroup = async () => {
    try {
      this.setState({ paLoading: true })
      const response = await api.get(`/group/${this.props.groupId}`)
      this.setState({
        groupDetails: response.data,
        paLoading: false,
      })
    } catch (error) {
      if (error.response?.status === 403) {
        this.setState({ login: true })
      }
      this.setState({ paLoading: false })
      console.log(error)
    }
  }

  createData = (key, value) => ({ key, value })

  handleRemoveImage = (imageId) => {
    const images = this.state.images.filter((m) => m.id !== imageId)
    this.setState({ images })
  }

  handleClosePrintDialog = () => {
    this.setState({
      printAuthOpen: false,
      printInvoiceOpen: false,
      images: [],
      prodList: [],
    })
  }

  openAuthorizationView = async () => {
    const visitId = this.state.selectedRows[0]
    this.getGroup()
    const patient = await this.getPatient(visitId)
    this.getAssessment(patient?.id)
    this.fetchImages(visitId)
    this.setState({ printAuthOpen: true })
  }

  openInvoiceView = () => {
    const visitId = this.state.selectedRows[0]
    this.getGroup()
    this.getPatient(visitId)
    this.setState({ printInvoiceOpen: true })
  }

  openPrintView = (mode) => {
    switch (mode) {
      case AUTHORIZE:
        this.openAuthorizationView()
        break
      case INVOICE:
        this.openInvoiceView()
        break
      default:
        this.setState({
          AuthButtOn: false,
          InvoiceButtOn: false,
          printAuthOpen: false,
          printInvoiceOpen: false,
        })
    }
  }

  getAuthorizationEditedRowsFromPrintView = () => {
    if (!this.componentRef) {
      return null
    }

    const normalize = (value) => String(value || '').replace(/\u00a0/g, ' ').trim()
    const readRows = (sectionName) => {
      const rowEls = Array.from(this.componentRef.querySelectorAll(`[data-email-section="${sectionName}"]`))
      return rowEls.map((rowEl) => {
        const keyEl = rowEl.querySelector('[data-email-role="key"]')
        const valueEl = rowEl.querySelector('[data-email-role="value"]')
        return {
          key: normalize(keyEl?.innerText || keyEl?.textContent),
          value: normalize(valueEl?.innerText || valueEl?.textContent),
        }
      })
    }

    return {
      supplRows1: readRows('supplRows1'),
      supplRows2: readRows('supplRows2'),
    }
  }

  getAuthorizationSupplementRows2 = () => {
    if (this.state.medicalAidRequiresDiscoveryPdf) {
      return []
    }

    return buildAuthorizationAssessmentRows(this.state.assessment, {
      omitWoundSpecific: false,
    }).map((row) => this.createData(row.key, row.value))
  }

  getDiscoveryPdfAttachment = async () => {
    if (!this.discoveryPdfRef) {
      return null
    }

    const { generatePdfAttachmentFromElement } = await import('../util/generatePdfAttachment')
    const patientName = `${this.state.patient?.firstName || ''} ${this.state.patient?.patientName || ''}`
      .trim()
      .replace(/[^a-z0-9._-]+/gi, '_') || 'patient'

    return generatePdfAttachmentFromElement(
      this.discoveryPdfRef,
      `${patientName}_wound_assessment.pdf`,
    )
  }

  showSnackbar = (message, severity = 'info') => {
    this.setState({
      snackbarMessage: String(message || ''),
      snackbarOpen: true,
      snackbarSeverity: severity,
    })
  }

  handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return
    }
    this.setState({ snackbarOpen: false })
  }

  getArchiveImagesForSend = async (isInvoice) => {
    if (isInvoice) {
      return []
    }

    let archiveImages = (this.state.images || [])
      .map((image) => ({
        imageUrl: image?.imageUrl,
        title: image?.title,
      }))
      .filter((image) => String(image.imageUrl || '').trim() !== '')

    if (archiveImages.length > 0) {
      return archiveImages
    }

    const visitId = this.state.selectedRows?.[0]
    if (!visitId) {
      return []
    }

    try {
      const response = await api.get(`/photoBatch/${visitId}`)
      const fetchedImages = response.data || []
      this.setState({ images: fetchedImages })
      archiveImages = fetchedImages
        .map((image) => ({
          imageUrl: image?.imageUrl,
          title: image?.title,
        }))
        .filter((image) => String(image.imageUrl || '').trim() !== '')
      return archiveImages
    } catch (error) {
      console.log('Failed to fetch archive images at send time:', error)
      return []
    }
  }

  handleSendEmail = async () => {
    if (this.state.emailSending) {
      return
    }

    if (!this.componentRef) {
      this.showSnackbar('Print content is not available yet. Please open the print view and try again.', 'warning')
      return
    }

    const isInvoice = this.state.printInvoiceOpen

    // ── SYNCHRONOUS: capture live DOM values before any async work ──
    const capturedAuthRows = !isInvoice
      ? this.getAuthorizationEditedRowsFromPrintView()
      : null
    // ── end synchronous capture ──

    const modeLabel = isInvoice ? 'Invoice' : 'Authorization'
    const patientName = `${this.state.patient?.firstName || ''} ${this.state.patient?.patientName || ''}`.trim()
    const groupName = this.state.groupDetails?.hdetail1 || 'Wound Care'
    const subject = isInvoice
      ? `Woundcare Invoice for ${patientName || 'Patient'}`.trim()
      : `Woundcare Authorization Request for ${patientName || 'Patient'}, membership no. ${this.state.patient?.memberNo || ''}`.trim()
    const text = isInvoice
      ? (
        `Hello,\n\n` +
        `please find the ${modeLabel.toLowerCase()} for ${patientName || 'the patient'}.\n` +
        `Regards,\n${groupName}`
      ) : (
        `Hello,\n\n` +
        `please find the ${modeLabel.toLowerCase()} information for ${patientName || 'the patient'}.\n` +
        `Clinical images are attached in a single PDF file.\n` +
        `Regards,\n${groupName}`
      )

    const logoInlineAttachment = await this.getLogoInlineAttachment()
    const discoveryPdfAttachment = !isInvoice && this.state.medicalAidRequiresDiscoveryPdf
      ? await this.getDiscoveryPdfAttachment()
      : null

    if (!isInvoice && this.state.medicalAidRequiresDiscoveryPdf && !discoveryPdfAttachment) {
      this.showSnackbar('Unable to generate the Discovery wound assessment PDF. Please try again.', 'error')
      return
    }

    let html
    if (isInvoice) {
      html = buildInvoiceEmailHtmlTemplate({
        dateString: this.state.dateString,
        groupDetails: this.state.groupDetails,
        patient: this.state.patient,
        prodList: this.state.prodList,
        totals: this.state.totals,
        logoInlineAttachment,
      })
    } else {
      const supplRows1 = capturedAuthRows?.supplRows1?.length > 0
        ? capturedAuthRows.supplRows1
        : this.state.supplRows1
      const shouldSuppressWoundHistory = Boolean(discoveryPdfAttachment)
      const supplRows2 = shouldSuppressWoundHistory
        ? []
        : (capturedAuthRows?.supplRows2?.length > 0
          ? capturedAuthRows.supplRows2
          : this.getAuthorizationSupplementRows2())
      html = buildAuthorizationEmailHtmlTemplate({
        assessment: this.state.assessment,
        dateString: this.state.dateString,
        fullName: this.state.fullName,
        groupDetails: this.state.groupDetails,
        images: [],
        patient: this.state.patient,
        prodList: this.state.prodList,
        prodListColumns,
        supplRows1,
        supplRows2,
        logoInlineAttachment,
      })
    }

    const inlineAttachments = [logoInlineAttachment].filter(Boolean)
    const archiveImages = await this.getArchiveImagesForSend(isInvoice)
    const archiveName = archiveImages.length > 0
      ? `${(patientName || 'patient').replace(/[^a-z0-9._-]+/gi, '_')}_${(this.state.dateString || 'visit').replace(/[^a-z0-9._-]+/gi, '_')}_clinical_images.pdf`
      : undefined

    const recipientEmail = isInvoice
      ? String(this.state.patient?.email || '').trim()
      : this.state.medicalAidEmail
    if (!recipientEmail) {
      const missingEmailMessage = isInvoice
        ? `No email address found for patient "${patientName || 'unknown'}". Please add the patient's email address and try again.`
        : `No email address found for medical aid "${this.state.patient?.medicalAid || 'unknown'}". Please add the email address in the Medical Aids list and try again.`
      this.showSnackbar(missingEmailMessage, 'warning')
      return
    }

    try {
      this.setState({ emailSending: true })
      await api.post('/email/send', {
        to: recipientEmail,
        subject,
        text,
        html,
        ...(discoveryPdfAttachment || {}),
        inlineAttachments,
        ...(archiveImages.length > 0 ? { archiveImages, archiveName } : {}),
        mailConfigSet: DEMO_MAIL_CONFIG_SET,
      })
      if (!isInvoice) {
        const patientId = this.state.patient?.id
        if (patientId) {
          const { id, ...patientData } = this.state.patient
          const updatedPatientData = {
            ...patientData,
            authStatus: 'pending',
          }
          try {
            await api.put(`/patient/${patientId}`, updatedPatientData)
          } catch (updateError) {
            console.log('Failed to update patient authStatus:', updateError)
          }
        }
      }
      this.showSnackbar(`Email sent successfully to ${recipientEmail}.`, 'success')
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to send email.'
      this.showSnackbar(message, 'error')
      console.log(error)
    } finally {
      this.setState({ emailSending: false })
    }
  }

  render() {
    const { classes } = this.props
    const {
      visitsMap,
      dateString,
      fullName,
      groupDetails,
      paLoading,
      patient,
      emailSending,
      printAuthOpen,
      printInvoiceOpen,
      sbLoading,
      viLoading,
      filterModalOpen,
    } = this.state

    const handleVisitSelectionChange = (selection) => {
      let thorough = true

      for (let x = 1; x < selection.length; x++) {
        if (visitsMap[selection[0]]?.SAID !== visitsMap[selection[x]]?.SAID) {
          thorough = false
          break
        }
      }

      this.setState({
        InvoiceButtOn: thorough && selection.length > 0,
        AuthButtOn: selection.length === 1,
        selectedRows: selection,
      })
    }

    const supplRows2 = this.getAuthorizationSupplementRows2()

    const fileName = `${patient.patientName}_${patient.firstName}_${dateString}`

    const ReactToPrintStub = (
      <ReactToPrint content={() => this.componentRef} documentTitle={fileName} removeAfterPrint='true'>
        <PrintContextConsumer>
          {({ handlePrint }) => (
            <button className={classes.butto} onClick={handlePrint}>
              Print PDF
            </button>
          )}
        </PrintContextConsumer>
      </ReactToPrint>
    )

    const emailButton = (
      <button
        className={classes.buttoSecondary}
        onClick={this.handleSendEmail}
        disabled={emailSending}
      >
        {emailSending ? (
          <CircularProgress size={16} color="inherit" style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
        ) : (
          <EmailIcon style={{ fontSize: 18, marginRight: 8, verticalAlign: 'text-bottom' }} />
        )}
        {emailSending ? 'Sending...' : 'Send Email'}
      </button>
    )

    const appBar = (
      <AppBar position="static">
        <Toolbar variant="dense">
          <Tooltip title="Close Modal" placement="top">
           <IconButton
            edge="start"
            color="inherit"
            onClick={this.handleClosePrintDialog}
            aria-label="close"
            size="large"
           >
            <CloseIcon />
           </IconButton>
          </Tooltip>
          <Typography variant="h6" className={classes.poptitle}>
            {'Print View'}
          </Typography>
          {emailButton}
          {ReactToPrintStub}
        </Toolbar>
      </AppBar>
    )

    if (viLoading && !filterModalOpen) {
      return (
        <main className={classes.content}>
          <div className={classes.toolbar} />
          <CircularProgress size={150} className={classes.uiProgess} />
          <AuthorizeFilterModal
            open={filterModalOpen}
            onClose={this.closeFilterModal}
            onChanged={this.handleFilterChanged}
            hasResults={this.state.visits.length > 0}
          />
        </main>
      )
    }

    return (
      <main className={classes.content}>
        <div className={classes.toolbar} />

        <Grid container item xs={12} spacing={0}>
          <Grid item xs={6}>
            {this.state.error === false ? (
              <div className={classes.subTitle}>SELECT CARE PLAN TO AUTHORIZE</div>
            ) : (
              <div className={classes.error}>Wrong Input!</div>
            )}
          </Grid>
          <Grid item xs={2}>
           <Tooltip title="Filter visits" placement="top">
            <IconButton
              className={classes.selectButton}
              color="primary"
              aria-label="Filter visits"
              onClick={this.openFilterModal}
              size="large"
            >
              <FilterListIcon style={{ fontSize: 42 }} />
            </IconButton>
           </Tooltip>
          </Grid>
          <Grid item xs={2}>
            {this.state.AuthButtOn ? (
             <Tooltip title="Authorize visit">
              <IconButton
                className={classes.selectButton}
                color="primary"
                aria-label="Authorize visit"
                onClick={() => this.openPrintView(AUTHORIZE)}
                size="large"
              >
                <LaunchIcon style={{ fontSize: 60 }} />
              </IconButton>
             </Tooltip>
            ) : ''}
          </Grid>
          <Grid item xs={2}>
            {this.state.InvoiceButtOn ? (
             <Tooltip title="Invoice visit" placement="top">
              <IconButton
                className={classes.selectButton}
                color="primary"
                aria-label="Invoice visit"
                onClick={() => this.openPrintView(INVOICE)}
                size="large"
              >
                <ReceiptLongIcon style={{ fontSize: 60 }} />
              </IconButton>
             </Tooltip>
            ) : ''}
          </Grid>
        </Grid>

        <div style={{ height: `${vHeight}pt`, width: `${vWidth}pt`, borderRadius: 20, overflow: 'hidden' }}>
          <DataGrid
            className={classes.visitGrid}
            rows={this.state.visits}
            columns={visitColumns}
            isRowSelectable={() => true}
            checkboxSelection
            onSelectionModelChange={handleVisitSelectionChange}
          />
        </div>

        <Dialog
          fullScreen
          open={printAuthOpen}
          onClose={this.handleClosePrintDialog}
          TransitionComponent={Transition}
        >
          {!this.state.printInvoiceOpen && this.state.medicalAidRequiresDiscoveryPdf && (
            <div
              ref={(el) => { this.discoveryPdfRef = el }}
              aria-hidden="true"
              style={{
                position: 'fixed',
                left: '-10000px',
                top: 0,
                width: '1200px',
                background: '#ffffff',
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              <AssessmentEditor
                classes={{}}
                assessment={this.state.assessment}
                patient={this.state.patient}
                groupDetails={this.state.groupDetails}
                onChange={() => {}}
                pdfMode
              />
            </div>
          )}
          {appBar}
          <div style={{ height: 1500, width: '500%' }} ref={(el) => (this.componentRef = el)}>
            <p />
            {sbLoading && <CircularProgress size={150} className={classes.uiProgess} />}
            <AuthorizePages
              classes={classes}
              groupDetails={groupDetails}
              patient={patient}
              fullName={fullName}
              supplRows1={this.state.supplRows1}
              prodList={this.state.prodList}
              prodListColumns={prodListColumns}
              supplRows2={supplRows2}
              images={this.state.images}
              onRemoveImage={this.handleRemoveImage}
            />
          </div>
        </Dialog>

        <Dialog
          fullScreen
          open={printInvoiceOpen}
          onClose={this.handleClosePrintDialog}
          TransitionComponent={Transition}
        >
          {appBar}
          <div style={{ height: 1200, width: '500%' }} ref={(el) => (this.componentRef = el)}>
            <p />
            {sbLoading && <CircularProgress size={150} className={classes.uiProgess} />}
            {!paLoading && (
              <InvoicePages
                classes={classes}
                groupDetails={groupDetails}
                patient={patient}
                dateString={dateString}
                prodList={this.state.prodList}
                prodListColumnsInv={prodListColumnsInv}
                totals={this.state.totals}
              />
            )}
          </div>
        </Dialog>

        <AuthorizeFilterModal
          open={filterModalOpen}
          onClose={this.closeFilterModal}
          onChanged={this.handleFilterChanged}
          hasResults={this.state.visits.length > 0}
        />
        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={5000}
          onClose={this.handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={this.handleSnackbarClose} severity={this.state.snackbarSeverity} variant="filled">
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </main>
    )
  }
}

export default withStyles(styles)(authorize)
